import React, { useMemo, useState } from 'react'
import icons from 'payments-icons-library'
import { Button, Tooltip, TooltipTrigger, TooltipContent } from '@shopflo/ui'
import { CaretDown } from '@phosphor-icons/react'
import { regexPatterns } from 'src/lib/utils/validations'
import { classNames } from 'src/lib/utils/helpers'
import { paymentMethodImages } from 'src/components/ui-customization/sub-components/payment-page/icons/PaymentMethodIcon'

const PAYMENT_TYPE_MAPPING = {
  cards: ['cardbanks'],
  wallets: ['wallet'],
  upi: ['upi'],
  netbanking: ['cardbanks']
} as const

type PaymentType = keyof typeof PAYMENT_TYPE_MAPPING

interface LogoItem {
  name: string
  url: string
}

interface LogoComponentProps {
  logos: (string | LogoItem)[]
  size: 'sm' | 'md' | 'lg' | 'xs'
}

const LogoComponent = ({ logos, size = 'sm' }: LogoComponentProps) => {
  const logoData = useMemo(() => {
    return logos
      .map(value => {
        if (!value) return null

        // Handle objects with name and url
        if (typeof value === 'object' && value.name && value.url) {
          return { url: value.url, name: value.name }
        }

        // Handle string values (existing logic)
        if (typeof value === 'string') {
          const isUrl = regexPatterns.isUrl.test(value)
          if (isUrl) return { url: value, name: 'Custom Logo' }
          try {
            const iconData = icons.getIcon(value.toLowerCase(), size)
            // Ensure we use the library's icon_name if available, otherwise fall back to the original value
            return {
              url: iconData?.icon_url,
              name: iconData?.icon_name || value
            }
          } catch (error) {
            console.warn(`Failed to resolve icon for ${value}:`, error)
            return null
          }
        }

        return null
      })
      .filter(Boolean) as Array<{ url: string; name: string }>
  }, [logos, size])

  const [showAll, setShowAll] = React.useState(false)
  const visibleLogos = showAll ? logoData : logoData.slice(0, 4)

  return (
    <div className='flex items-center gap-2 w-fit'>
      <div className='flex'>
        {visibleLogos.map((logoItem, index) => (
          <div
            key={logoItem.url}
            className={classNames('relative', index > 0 ? '-ml-2' : '', size === 'xs' ? '!-ml-1' : '')}
            style={{ zIndex: logoData.length - index }}
          >
            <img
              src={logoItem.url}
              className={classNames(
                'min-w-6 h-6 border-2 border-zinc-400/15 rounded-full',
                size === 'xs' ? 'max-w-4 max-h-4 !min-w-4 min-h-4' : ''
              )}
              alt='logo'
            />
          </div>
        ))}
      </div>
      {!showAll && logoData.length > 4 && (
        <Button className='ml-auto' size='sm' variant='ghost' onClick={() => setShowAll(true)}>
          View all
        </Button>
      )}
    </div>
  )
}

interface HighlightLogoProps {
  selectedLogos: LogoItem[]
  onValueChange: (logos: LogoItem[]) => void
  paymentType?: PaymentType
  mode: string
}

export const HighlightLogo = ({ selectedLogos, onValueChange, paymentType = 'cards', mode }: HighlightLogoProps) => {
  const availableIcons = useMemo(() => {
    if (paymentMethodImages[mode]) return []

    const cfTypes = PAYMENT_TYPE_MAPPING[paymentType]
    const allIcons: Array<{ name: string; url: string }> = []

    cfTypes.forEach(cfType => {
      try {
        const iconsData = icons.getModesIcons(cfType, 'sm')
        iconsData.forEach((icon: any) => {
          allIcons.push({
            name: icon.icon_name,
            url: icon.icon_url
          })
        })
      } catch (error) {
        console.warn(`Failed to get icons for ${cfType}:`, error)
      }
    })

    return allIcons
  }, [paymentType, mode])

  // Special handling for single logo payment modes
  const isSingleLogoMode = ['SIMPL', 'SIMPL_PL', 'SNPM', 'VELOCITY'].includes(mode)
  const maxLogos = isSingleLogoMode ? 1 : mode === 'UPI_INTENT' ? 4 : 3

  const handleLogoClick = (logoItem: LogoItem) => {
    const isSelected = selectedLogos.some(logo => logo.url === logoItem.url)

    if (isSelected) {
      const updated = selectedLogos.filter(logo => logo.url !== logoItem.url)
      onValueChange(updated)
    } else if (selectedLogos.length < maxLogos) {
      const updated = [...selectedLogos, logoItem]
      onValueChange(updated)
    }
  }

  const renderSelectedLogos = () => {
    return selectedLogos
      .map((logoItem, index) => {
        return (
          <Tooltip key={logoItem.url}>
            <TooltipTrigger asChild>
              <div
                className='flex py-3 px-6 bg-white rounded-xl cursor-pointer border-2 border-primary-dark shadow-sm shadow-primary-dark/20 relative hover:bg-gray-50 transition-colors min-w-[88px] items-center justify-center'
                onClick={() => handleLogoClick(logoItem)}
              >
                {!isSingleLogoMode && (
                  <div className='flex bg-primary-dark rounded-full text-white w-3 h-3 items-center justify-center text-text-xxs-regular absolute top-1 right-1'>
                    {index + 1}
                  </div>
                )}
                <img src={logoItem.url} className='w-6 h-6 flex items-center justify-center' alt={'logo'} />
              </div>
            </TooltipTrigger>
            <TooltipContent>{logoItem.name}</TooltipContent>
          </Tooltip>
        )
      })
      .filter(Boolean)
  }

  const filteredLogos = () => {
    const availableLogos = availableIcons.filter(icon => !selectedLogos.some(selected => selected.url === icon.url))

    // For single logo modes, show only the mode-specific logo, never the generic library icons
    if (isSingleLogoMode) {
      if (selectedLogos.length > 0) return []
      const modeImageUrl = paymentMethodImages[mode]
      if (!modeImageUrl) return []
      const modeIcon: LogoItem = { name: mode, url: modeImageUrl }
      return (
        <Tooltip key={modeIcon.url}>
          <TooltipTrigger asChild>
            <div
              className='py-3 flex px-6 rounded-xl cursor-pointer transition-all border-2 border-transparent min-w-[88px] items-center justify-center bg-zinc-400/15 hover:bg-zinc-400/25'
              onClick={() => handleLogoClick(modeIcon)}
            >
              <img src={modeIcon.url} className='w-6 h-6' alt={modeIcon.name} />
            </div>
          </TooltipTrigger>
          <TooltipContent>{modeIcon.name}</TooltipContent>
        </Tooltip>
      )
    }

    const minTotalLogos = 4
    const minAvailableToShow = Math.max(0, minTotalLogos - selectedLogos.length)
    const itemsToRender = showAllAvailable ? availableLogos : availableLogos.slice(0, minAvailableToShow)

    return itemsToRender.map(iconData => {
      const isDisabled = selectedLogos.length >= maxLogos

      return (
        <Tooltip key={iconData.url}>
          <TooltipTrigger asChild>
            <div
              className={classNames(
                'py-3 flex px-6 rounded-xl cursor-pointer transition-all border-2 border-transparent min-w-[88px] items-center justify-center',
                isDisabled ? 'bg-gray-200 cursor-not-allowed opacity-50' : 'bg-zinc-400/15 hover:bg-zinc-400/25'
              )}
              onClick={() => !isDisabled && handleLogoClick(iconData)}
            >
              <img src={iconData.url} className='w-6 h-6' alt={iconData.name} />
            </div>
          </TooltipTrigger>
          <TooltipContent>{iconData.name}</TooltipContent>
        </Tooltip>
      )
    })
  }

  const [showAllAvailable, setShowAllAvailable] = useState(false)
  const availableLogos = availableIcons.filter(icon => !selectedLogos.some(selected => selected.url === icon.url))
  const minTotalLogos = 4
  const minAvailableToShow = Math.max(0, minTotalLogos - selectedLogos.length)
  const shouldShowViewAll = !isSingleLogoMode && !showAllAvailable && availableLogos.length > minAvailableToShow

  return (
    <div className='flex flex-col gap-3'>
      <div className='grid w-full gap-2 grid-cols-[repeat(auto-fill,minmax(88px,1fr))]'>
        {renderSelectedLogos()}
        {filteredLogos()}
        {shouldShowViewAll && (
          <div className='flex justify-end self-center items-end w-full col-span-full'>
            <Button size='sm' variant='ghost' onClick={() => setShowAllAvailable(true)} rightIcon={CaretDown}>
              View all
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default LogoComponent
